import React from "react";

const SPORTS_NAV_ITEMS = [
  { key: "facilities", label: "Facilities" },
  { key: "grounds", label: "Arenas" },
  { key: "gallery", label: "Gallery" },
  { key: "pricing", label: "Pricing" },
  { key: "location", label: "Location" },
];

const SportsTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = "clean",
  primaryColor = "#33A444",
  accentColor = "#111827",
  heroImage = "",
  headline,
  subheadline,
  ctaLabel = "Book Now",
  openBooking,
  sectionRenderers = {},
  themeLayout = "sports",
  selectedThemeLayout = "sports",
}) => {
  const bookingEnabled = website?.booking?.enabled !== false;

  const visibleNavigation = SPORTS_NAV_ITEMS.filter(
    (item) => website?.sections?.[item.key] !== false
  );

  const orderedSections = Array.isArray(website?.sectionOrder)
    ? website.sectionOrder
        .filter((key) => key !== "hero")
        .filter((key) => website?.sections?.[key] !== false)
    : [];

  const city = venue?.location?.city || venue?.city || "Your city";
  const openingTime = venue?.policies?.openTime || "06:00";
  const closingTime = venue?.policies?.closeTime || "22:00";

  const sports = [
    ...new Set(
      grounds
        .map(
          (ground) =>
            ground?.sportName ||
            ground?.sport?.name ||
            ground?.sportType
        )
        .filter(Boolean)
    ),
  ];

  return (
    <main
      id="top"
      className={`pv-page pv-theme-${themeLayout} pv-bg-${backgroundStyle}`}
      data-theme={themeLayout}
      data-selected-theme={selectedThemeLayout}
      style={{
        "--pv-primary": primaryColor,
        "--pv-accent": accentColor,
      }}
    >
      <header className="pvs-nav-shell">
        <nav className="pvs-nav" aria-label="Venue website navigation">
          <a className="pvs-brand" href="#top">
            <span className="pvs-brand-icon" aria-hidden="true">
              OG
            </span>

            <span>
              <strong>{venue?.name || "Sports Venue"}</strong>
              <small>{city}</small>
            </span>
          </a>

          <div className="pvs-nav-links">
            <a href="#top">Home</a>

            {visibleNavigation.map((item) => (
              <a href={`#${item.key}`} key={item.key}>
                {item.label}
              </a>
            ))}
          </div>

          {bookingEnabled && (
            <button
              type="button"
              className="pvs-nav-cta"
              onClick={() => openBooking?.()}
            >
              {ctaLabel}
            </button>
          )}
        </nav>
      </header>

      {website?.sections?.hero !== false && (
        <section className="pvs-hero">
          <div className="pvs-hero-media">
            {heroImage ? (
              <img src={heroImage} alt={venue?.name || "Sports venue"} />
            ) : (
              <div className="pvs-hero-placeholder">
                <span>Game day starts here</span>
                <strong>{venue?.name || "Your Arena"}</strong>
              </div>
            )}

            <div className="pvs-hero-shade" />
            <div className="pvs-field-lines" aria-hidden="true" />
          </div>

          <div className="pvs-hero-content">
            <span className="pvs-kicker">
              Book&nbsp;&nbsp;•&nbsp;&nbsp;Play&nbsp;&nbsp;•&nbsp;&nbsp;Compete
            </span>

            <h1>{headline}</h1>

            <p>{subheadline}</p>

            <div className="pvs-hero-actions">
              {bookingEnabled && (
                <button
                  type="button"
                  className="pvs-primary-action"
                  onClick={() => openBooking?.()}
                >
                  <span>{ctaLabel}</span>
                  <strong aria-hidden="true">→</strong>
                </button>
              )}

              {venue?.googleMapsUrl && (
                <a
                  className="pvs-secondary-action"
                  href={venue.googleMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Find the venue
                </a>
              )}
            </div>

            <div className="pvs-scoreboard">
              <div>
                <strong>{grounds.length}</strong>
                <span>Active arenas</span>
              </div>

              <div>
                <strong>{sports.length || 1}</strong>
                <span>Sports available</span>
              </div>

              <div>
                <strong>{openingTime}</strong>
                <span>First session</span>
              </div>

              <div>
                <strong>{closingTime}</strong>
                <span>Last session</span>
              </div>
            </div>
          </div>

          <div className="pvs-hero-label" aria-hidden="true">
            <span>PLAY</span>
            <span>HARD</span>
          </div>
        </section>
      )}

      <section className="pvs-performance-strip">
        <div>
          <span>01</span>
          <strong>Instant availability</strong>
        </div>

        <div>
          <span>02</span>
          <strong>Multiple slot booking</strong>
        </div>

        <div>
          <span>03</span>
          <strong>Professional facilities</strong>
        </div>

        <div>
          <span>04</span>
          <strong>Secure reservation</strong>
        </div>
      </section>

      <div className="pv-content pvs-content">
        {orderedSections.map((key) => {
          const renderSection = sectionRenderers[key];

          return renderSection ? (
            <React.Fragment key={key}>
              {renderSection()}
            </React.Fragment>
          ) : null;
        })}
      </div>

      <section className="pvs-final-cta">
        <div>
          <span>Ready for the next game?</span>
          <h2>Own the field.</h2>
        </div>

        {bookingEnabled && (
          <button type="button" onClick={() => openBooking?.()}>
            {ctaLabel}
          </button>
        )}
      </section>

      <footer className="pvs-footer">
        <div className="pvs-footer-brand">
          <span className="pvs-brand-icon" aria-hidden="true">
            OG
          </span>

          <div>
            <strong>{venue?.name || "Sports Venue"}</strong>
            <small>{city}</small>
          </div>
        </div>

        <div className="pvs-footer-details">
          <span>
            Open {openingTime} – {closingTime}
          </span>
          <span>{grounds.length} bookable arena{grounds.length === 1 ? "" : "s"}</span>
          <span>Powered by OnGround</span>
        </div>

        <div className="pvs-footer-bottom">
          © {new Date().getFullYear()} {venue?.name || "Sports Venue"}
        </div>
      </footer>
    </main>
  );
};

export default SportsTheme;